import { useEffect, useReducer, useState } from "react"
import { useLogisticsPermissions } from "@features/delivery-stop/hooks/use-logistics-permissions"
import { ActionIcon, Badge, Button, Flex, Group, Modal, ScrollArea, Select, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { IconEdit, IconPlus, IconPower, IconSearch, IconX } from "@tabler/icons-react"
import { showNotification } from "@mantine/notifications"
import { openConfirmModal } from "@mantine/modals"
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, PAGE_SIZES } from "@/shared"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import { headerStore } from "@shared/state/header.state.ts"
import { useActivateRider, useCreateRider, useDeactivateRider, useRiders, useUpdateRider } from "../hooks/use-riders"
import { Rider, RIDER_TYPE, RIDER_TYPE_LABEL, SaveRiderDto } from "../domain/rider.types"
import { DEFAULT_RIDER_SCHEDULE, RiderScheduleSlot, describeSchedule, effectiveSchedule, isSameSchedule, sortSchedule, validateRiderSchedule } from "../domain/rider-schedule"
import { RiderScheduleEditor } from "../components/RiderScheduleEditor"
import { centerLabel, centerOptions } from "@features/delivery-stop/utils/logistics-center"
import { useCountryScope } from "@/shared/country/use-country-scope"
import { COUNTRY_OPTIONS, countryOf, countryQueryParam, createCountry } from "@/shared/country/country-scope"
import { CountryBadge, CountryFilterSelect } from "@/shared/country/country-components"

interface SearchState {
  search: string
  type: string
  isActive: string
  /** "Perú" | "Ecuador" | "" (los dos). Solo lo usa el admin */
  country: string
}

interface State {
  search: SearchState
  page: number
  rows: number
  sortStatus: DataTableSortStatus<Rider>
}

type Action = { type: "SET_SEARCH"; payload: Partial<SearchState> } | { type: "SET_PAGE"; payload: number } | { type: "SET_ROWS"; payload: number } | { type: "SET_SORT"; payload: DataTableSortStatus<Rider> }

const initialState: State = {
  search: { search: "", type: "", isActive: "", country: "" },
  page: DEFAULT_PAGE,
  rows: DEFAULT_PAGE_SIZE,
  sortStatus: { columnAccessor: "name", direction: "asc" }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_ROWS":
      return { ...state, rows: action.payload, page: 1 }
    case "SET_SORT":
      return { ...state, sortStatus: action.payload }
    default:
      return state
  }
}

const emptyRider: SaveRiderDto = { name: "", type: RIDER_TYPE.STAFF, phone: "", vehiclePlate: "", notes: "", supabaseUserId: "" }

export default function RidersPage() {
  const { tableHeight } = useDynamicHeight()
  const [state, dispatch] = useReducer(reducer, initialState)
  // País (EC-15): el admin ve la columna, filtra y elige el país de cada repartidor; al resto el back le da los de su país y crea en él
  const countryScope = useCountryScope()
  const canChooseCountry = countryScope.canSeeAllCountries

  const { data, isFetching, refetch } = useRiders({
    page: state.page,
    rows: state.rows,
    sortColumn: state.sortStatus.columnAccessor,
    sortDirection: state.sortStatus.direction,
    search: state.search.search || undefined,
    type: state.search.type || undefined,
    isActive: state.search.isActive ? state.search.isActive === "true" : undefined,
    country: countryQueryParam(countryScope, state.search.country)
  })
  const createRiderMutation = useCreateRider()
  const updateRiderMutation = useUpdateRider()
  const deactivateRiderMutation = useDeactivateRider()
  const activateRiderMutation = useActivateRider()

  const [opened, setOpened] = useState(false)
  const [editingRider, setEditingRider] = useState<Rider | null>(null)
  const [form, setForm] = useState<SaveRiderDto>(emptyRider)
  const [schedule, setSchedule] = useState<RiderScheduleSlot[]>(DEFAULT_RIDER_SCHEDULE)
  const [initialSchedule, setInitialSchedule] = useState<RiderScheduleSlot[]>(DEFAULT_RIDER_SCHEDULE)

  useEffect(() => {
    headerStore.setTitle("Repartidores")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const permisos = useLogisticsPermissions()

  const handleSave = async () => {
    if (!form.name?.trim()) return

    // El horario solo viaja si cambió (guardarlo reenvía el horario a la app). Igual al de por defecto = volver al de por defecto (null)
    const scheduleChanged = !isSameSchedule(schedule, initialSchedule)
    if (scheduleChanged) {
      const scheduleError = validateRiderSchedule(schedule)
      if (scheduleError) {
        showNotification({ title: "Horario no válido", message: scheduleError, color: "red" })
        return
      }
    }
    const scheduleToSend = isSameSchedule(schedule, DEFAULT_RIDER_SCHEDULE) ? null : sortSchedule(schedule)
    // Solo el admin y el binacional (EC-19) mandan el país (por defecto Perú el admin, su país principal el binacional); los demás no lo mandan y el back crea en el suyo
    const baseData: SaveRiderDto = canChooseCountry ? { ...form, country: createCountry(countryScope, form.country) } : { ...form, country: undefined }
    const riderData: SaveRiderDto = scheduleChanged ? { ...baseData, schedule: scheduleToSend } : baseData

    if (editingRider) {
      await updateRiderMutation.mutateAsync({ id: editingRider.id, riderData })
    } else {
      await createRiderMutation.mutateAsync(scheduleChanged && scheduleToSend ? riderData : baseData)
    }
    setOpened(false)
    setEditingRider(null)
    setForm(emptyRider)
    setSchedule(DEFAULT_RIDER_SCHEDULE)
    setInitialSchedule(DEFAULT_RIDER_SCHEDULE)
  }

  const handleEdit = (rider: Rider) => {
    setEditingRider(rider)
    setForm({ name: rider.name, type: rider.type, phone: rider.phone ?? "", vehiclePlate: rider.vehiclePlate ?? "", notes: rider.notes ?? "", supabaseUserId: rider.supabaseUserId ?? "", country: countryOf(rider.country), center: rider.center ?? null })
    const current = effectiveSchedule(rider.schedule)
    setSchedule(current)
    setInitialSchedule(current)
    setOpened(true)
  }

  const handleDeactivate = (rider: Rider) => {
    openConfirmModal({
      title: "Desactivar repartidor",
      centered: true,
      children: (
        <Text size="sm">
          ¿Desactivar a <strong>{rider.name}</strong>? Ya no aparecerá disponible para asignar nuevas paradas, pero las paradas que ya tiene asignadas no se ven afectadas.
        </Text>
      ),
      labels: { confirm: "Desactivar", cancel: "Cancelar" },
      confirmProps: { color: "red", loading: deactivateRiderMutation.isPending, leftSection: <IconPower size={15} /> },
      onConfirm: async () => {
        await deactivateRiderMutation.mutateAsync(rider.id)
      }
    })
  }

  const handleActivate = (rider: Rider) => {
    openConfirmModal({
      title: "Reactivar repartidor",
      centered: true,
      children: (
        <Text size="sm">
          ¿Reactivar a <strong>{rider.name}</strong>? Volverá a aparecer disponible para asignar nuevas paradas.
        </Text>
      ),
      labels: { confirm: "Reactivar", cancel: "Cancelar" },
      confirmProps: { color: "green", loading: activateRiderMutation.isPending, leftSection: <IconPower size={15} /> },
      onConfirm: async () => {
        await activateRiderMutation.mutateAsync(rider.id)
      }
    })
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end" align="center">
        {permisos.riderCreate && (
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              setEditingRider(null)
              setForm(emptyRider)
              setSchedule(DEFAULT_RIDER_SCHEDULE)
              setInitialSchedule(DEFAULT_RIDER_SCHEDULE)
              setOpened(true)
            }}
          >
            Agregar Repartidor
          </Button>
        )}
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={isFetching}
        records={data.data}
        totalRecords={data.totalRecords || 0}
        recordsPerPage={state.rows}
        page={state.page}
        onPageChange={(page) => dispatch({ type: "SET_PAGE", payload: page })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(rows) => dispatch({ type: "SET_ROWS", payload: rows })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(sortStatus) => dispatch({ type: "SET_SORT", payload: sortStatus })}
        pinLastColumn
        columns={[
          {
            accessor: "name",
            title: "Nombre",
            sortable: true,
            filtering: state.search.search !== "",
            filter: (
              <TextInput
                placeholder="Buscar por nombre"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.search && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { search: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.search}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { search: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "type",
            title: "Tipo",
            sortable: true,
            filtering: state.search.type !== "",
            render: (record) => <Badge variant="light">{RIDER_TYPE_LABEL[record.type] || record.type}</Badge>,
            filter: (
              <Select
                comboboxProps={{ withinPortal: false }}
                clearable
                placeholder="Buscar por tipo"
                value={state.search.type}
                onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { type: value || "" } })}
                data={Object.values(RIDER_TYPE).map((type) => ({ value: type, label: RIDER_TYPE_LABEL[type] }))}
              />
            )
          },
          {
            accessor: "country",
            title: "País",
            hidden: !canChooseCountry,
            filtering: !!state.search.country,
            render: (record) => <CountryBadge country={record.country} />,
            filter: <CountryFilterSelect value={state.search.country} onChange={(country) => dispatch({ type: "SET_SEARCH", payload: { country } })} />
          },
          { accessor: "center", title: "Centro", render: (record) => centerLabel(record.center) || "-" },
          { accessor: "phone", title: "Teléfono" },
          { accessor: "vehiclePlate", title: "Placa" },
          {
            accessor: "schedule",
            title: "Horario",
            render: (record) => (
              <Text size="sm" c={record.schedule ? undefined : "dimmed"}>
                {describeSchedule(record.schedule)}
              </Text>
            )
          },
          {
            accessor: "isActive",
            title: "Estado",
            sortable: true,
            filtering: state.search.isActive !== "",
            render: (record) => <Badge color={record.isActive ? "green" : "gray"}>{record.isActive ? "Activo" : "Inactivo"}</Badge>,
            filter: (
              <Select
                comboboxProps={{ withinPortal: false }}
                clearable
                placeholder="Buscar por estado"
                value={state.search.isActive}
                onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { isActive: value || "" } })}
                data={[
                  { value: "true", label: "Activo" },
                  { value: "false", label: "Inactivo" }
                ]}
              />
            )
          },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !permisos.riderEdit,
            render: (rider) => (
              <Group gap={4} justify="right" wrap="nowrap">
                <Tooltip label="Editar repartidor">
                  <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => handleEdit(rider)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
                {rider.isActive ? (
                  <Tooltip label="Desactivar repartidor">
                    <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDeactivate(rider)}>
                      <IconPower size={16} />
                    </ActionIcon>
                  </Tooltip>
                ) : (
                  <Tooltip label="Reactivar repartidor">
                    <ActionIcon size="sm" variant="subtle" color="green.7" onClick={() => handleActivate(rider)}>
                      <IconPower size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
        style={{ height: tableHeight }}
        noRecordsText="No se encontraron repartidores"
      />

      <Modal opened={opened} onClose={() => setOpened(false)} title={editingRider ? "Editar Repartidor" : "Agregar Repartidor"} centered size="lg">
        <Stack>
          <TextInput label="Nombre" placeholder="Nombre completo, o etiqueta (ej. Almacén, Chaski)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Select
            label="Tipo"
            data={Object.values(RIDER_TYPE).map((type) => ({ value: type, label: RIDER_TYPE_LABEL[type] }))}
            value={form.type}
            onChange={(value) => setForm({ ...form, type: (value as SaveRiderDto["type"]) || RIDER_TYPE.STAFF })}
            allowDeselect={false}
          />
          {canChooseCountry && (
            <Select
              label="País"
              description="Solo se le asignan puntos de su país y aparece en el mapa y la programación de ese país"
              data={COUNTRY_OPTIONS}
              value={createCountry(countryScope, form.country)}
              onChange={(value) => setForm({ ...form, country: countryOf(value) })}
              allowDeselect={false}
            />
          )}
          {form.type === RIDER_TYPE.STAFF && (
            <Group grow>
              <TextInput label="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <TextInput label="Placa del vehículo" value={form.vehiclePlate} onChange={(e) => setForm({ ...form, vehiclePlate: e.target.value })} />
            </Group>
          )}
          <Select
            label="Centro"
            description="Solo se le asignan puntos de su centro (a un punto de Lima no va un repartidor de Trujillo). Vacío = sin restricción"
            data={centerOptions(countryOf(createCountry(countryScope, form.country)))}
            value={form.center ?? null}
            onChange={(value) => setForm({ ...form, center: value })}
            clearable
            placeholder="Sin centro"
          />
          <TextInput label="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <TextInput
            label="ID Supabase (app móvil)"
            description="UUID del usuario en Supabase para recibir notificaciones push. Dejar vacío si el repartidor no usa la app."
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={form.supabaseUserId}
            onChange={(e) => setForm({ ...form, supabaseUserId: e.target.value })}
          />
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="sm" fw={500}>
                Horario de trabajo
              </Text>
              <Button size="compact-xs" variant="subtle" onClick={() => setSchedule(DEFAULT_RIDER_SCHEDULE)}>
                Restablecer (lun a sáb 07:00–22:00)
              </Button>
            </Group>
            <Text size="xs" c="dimmed" mb="xs">
              La ubicación del repartidor en la app solo va encendida dentro de este horario, o mientras tenga un punto asignado sin terminar o en camino. Fuera de eso se apaga.
            </Text>
            <ScrollArea.Autosize mah={360} type="auto" offsetScrollbars>
              <RiderScheduleEditor value={schedule} onChange={setSchedule} />
            </ScrollArea.Autosize>
          </div>
        </Stack>

        <Button fullWidth mt="xl" onClick={handleSave} loading={createRiderMutation.isPending || updateRiderMutation.isPending}>
          Guardar Repartidor
        </Button>
      </Modal>
    </Stack>
  )
}
